import * as R from 'ramda';
import {
  takeLatest,
  put,
  select,
  call,
  delay,
  take,
} from 'redux-saga/effects';
import { generativeAi, ti2 as ti2Apis, matcher as matcherApis } from '../../../../api.js';
import { apiSaga, apiSagaJob } from '../../../../root-sagas.js';
import { _getAccountInfo } from './edit-app-key-configuration-sagas.js';
import {
  defaultItineraryMining,
  normalizeItineraryDraftForPersistence,
} from './itinerary-mining-reducer';
import { isTourPlanNxIntegration, TERMINAL_STATUSES } from './itinerary-mining-utils';

function* getPartnerContext() {
  let metadata = yield select(state => state.generativeAi.partnerSettings.metadata);
  if (!metadata) return null;
  const { partnerId } = metadata;
  if (!partnerId) return null;
  if (!metadata.integrationApp) {
    yield take('genAi_partnerSettings_loadAllAppKeys_SUCCESS');
    metadata = yield select(state => state.generativeAi.partnerSettings.metadata);
    if (!metadata) return null;
  }
  if (!metadata.integrationApp) return null;
  return {
    partnerId: metadata.partnerId,
    integrationApp: metadata.integrationApp,
  };
}

function* initItineraryMining() {
  yield put({ type: 'genAi_itineraryMining_reset' });
  const context = yield call(getPartnerContext);
  if (!context) return;
  yield put({ type: 'genAi_itineraryMining_loadConfig' });
  yield put({ type: 'genAi_itineraryMining_loadJobStatus' });
  yield put({ type: 'genAi_itineraryMining_loadAffiliateAgents' });
  yield put({ type: 'genAi_itineraryMining_loadLastQuery' });
}

function* loadLastEnrichmentQuery() {
  const context = yield call(getPartnerContext);
  if (!context) return;
  const { partnerId, integrationApp } = context;
  yield apiSaga('genAi_itineraryMining_loadLastQuery', generativeAi.itineraryMiningLastQuery, [{
    partnerId,
    integrationId: integrationApp,
    skipLoader: true,
  }]);
}

/**
 * Refresh the persisted enrichment_query.json once a run reaches a terminal
 * status, regardless of which path delivered the status update (polling loop,
 * immediate setJob, hydrated job state on remount, etc.). Dedupe by jobId so
 * we only fire one refresh per terminal transition (a single run can emit
 * multiple progress updates with the same terminal status during shutdown).
 */
function* watchJobProgressForLastQueryRefresh() {
  let lastRefreshKey = null;
  while (true) {
    const action = yield take('genAi_itineraryMining_jobProgress');
    const status = String(action?.payload?.status || '').toLowerCase();
    const jobId = action?.payload?.jobId || null;
    if (!status) continue;
    if (!TERMINAL_STATUSES.has(status)) {
      // When status updates do not include jobId, treat any non-terminal
      // transition as a new run boundary so future terminal statuses can
      // trigger another refresh.
      if (!jobId && lastRefreshKey && lastRefreshKey.startsWith('status:')) {
        lastRefreshKey = null;
      }
      continue;
    }
    const refreshKey = jobId ? `job:${jobId}` : `status:${status}`;
    if (refreshKey === lastRefreshKey) continue;
    lastRefreshKey = refreshKey;
    yield put({ type: 'genAi_itineraryMining_loadLastQuery' });
  }
}

function* loadAffiliateAgents() {
  const context = yield call(getPartnerContext);
  if (!context) {
    return;
  }
  yield put({ type: 'genAi_itineraryMining_loadAffiliateAgents_STARTED' });
  if (!isTourPlanNxIntegration(context.integrationApp)) {
    yield put({ type: 'genAi_itineraryMining_loadAffiliateAgents_SUCCESS', payload: [] });
    return;
  }
  const { partnerId, integrationApp } = context;
  try {
    const { companyId, ti2token } = yield call(_getAccountInfo);
    const result = yield call(ti2Apis.getAffiliateAgents, {
      companyId,
      ti2token,
      app: integrationApp,
      hint: partnerId,
    });
    if (result && result.error) {
      throw new Error(R.pathOr('Unable to load agents.', ['json', 'message'], result.error) || 'Unable to load agents.');
    }
    // callApi / callTi2Api return { response: body }; body may be { agents } or a raw array
    const body = result && result.response != null ? result.response : {};
    const agents = Array.isArray(body) ? body : R.pathOr([], ['agents'], body);
    yield put({ type: 'genAi_itineraryMining_loadAffiliateAgents_SUCCESS', payload: agents });
  } catch (err) {
    yield put({
      type: 'genAi_itineraryMining_loadAffiliateAgents_ERROR',
      payload: err && err.message ? err.message : String(err),
    });
  }
}

function* loadConfig() {
  const context = yield call(getPartnerContext);
  if (!context) return;
  const { partnerId, integrationApp } = context;
  const { ti2token } = yield call(_getAccountInfo);
  yield apiSagaJob('genAi_itineraryMining_loadConfig', ti2Apis.getAppKey, [{
    app: integrationApp,
    hint: partnerId,
    ti2token,
    skipLoader: true,
  }]);
}

function* saveConfig({ payload = {} }) {
  const context = yield call(getPartnerContext);
  if (!context) return;
  const { partnerId, integrationApp } = context;
  const { companyId, ti2token } = yield call(_getAccountInfo);
  const itineraryState = yield select(state => state.generativeAi.partnerSettings.itineraryMining);
  const configuration = itineraryState.configuration || {};
  const draft = itineraryState.draft || defaultItineraryMining;
  const canonicalDraft = normalizeItineraryDraftForPersistence({
    ...draft,
    ...(payload.overrideDraft || {}),
  });
  const mergedConfiguration = {
    ...configuration,
    itinerary_mining: canonicalDraft,
  };
  const response = yield apiSaga('genAi_itineraryMining_saveConfig', ti2Apis.updateAppKey, [{
    companyId,
    ti2token,
    configuration: mergedConfiguration,
    hint: partnerId,
    app: integrationApp,
  }]);
  if (response) {
    yield put({ type: 'genAi_itineraryMining_applySavedConfig', payload: mergedConfiguration });
  }
}

function* runItineraryMiningJob({ payload = {} }) {
  const context = yield call(getPartnerContext);
  if (!context) return;
  const { partnerId, integrationApp } = context;
  const itineraryState = yield select(state => state.generativeAi.partnerSettings.itineraryMining);
  const draft = itineraryState?.draft || defaultItineraryMining;
  const canonicalDraft = normalizeItineraryDraftForPersistence(draft);
  if (!canonicalDraft.includeQuotes && !canonicalDraft.includeBookings) {
    yield put({
      type: 'genAi_itineraryMining_jobProgress',
      payload: {
        status: 'error',
        log: ['Select at least one source (quotes or bookings) before running itinerary mining.'],
      },
    });
    return;
  }
  const {
    includeQuotes,
    includeBookings,
    quotesLimit,
    bookingsLimit,
    travelDateRange,
    nxAgentFilterEnabled,
    nxAgentIds,
    bookingReferenceIds,
  } = canonicalDraft;
  const usePurchaseDateRangeFlag = Boolean(canonicalDraft.usePurchaseDateRange);
  const sendPurchaseDateRange = usePurchaseDateRangeFlag && Boolean(canonicalDraft.purchaseDateRange);
  const purchaseDateRange = sendPurchaseDateRange ? canonicalDraft.purchaseDateRange : {};
  const requestPayload = {
    partnerId,
    integrationId: integrationApp,
    options: {
      includeQuotes,
      includeBookings,
      quotesLimit,
      bookingsLimit,
      usePurchaseDateRange: usePurchaseDateRangeFlag,
      ...(sendPurchaseDateRange ? { purchaseDateRange } : {}),
      travelDateRange,
      nxAgentFilterEnabled,
      nxAgentIds,
      bookingReferenceIds,
    },
    ...payload,
  };
  const response = yield apiSaga('genAi_itineraryMining_runJob', generativeAi.itineraryMiningRun, [{ ...requestPayload, skipLoader: true }]);
  const jobs = R.pathOr([], ['jobs'], response);
  const job = jobs[0];
  if (job && job.jobId) {
    yield put({
      type: 'genAi_itineraryMining_setJob',
      payload: {
        jobId: job.jobId,
        status: job.status || 'pending',
        log: [],
      },
    });
    yield put({
      type: 'genAi_itineraryMining_startPolling',
      payload: { jobId: job.jobId },
    });
  } else {
    yield put({
      type: 'genAi_itineraryMining_runJob_ERROR',
      payload: 'Unable to start itinerary mining. Please try again.',
    });
  }
}

function* toggleItineraryEnabled({ payload }) {
  yield put({ type: 'genAi_itineraryMining_updateDraft', payload });
  yield call(saveConfig, { payload: {} });
}

function* toggleUseSelectedAgentId({ payload }) {
  yield put({ type: 'genAi_itineraryMining_updateDraft', payload });
  yield call(saveConfig, { payload: {} });
}

function* loadPersistedJobStatus() {
  const context = yield call(getPartnerContext);
  if (!context) return;
  const { partnerId, integrationApp } = context;
  const response = yield apiSaga('genAi_itineraryMining_loadJobStatus', generativeAi.itineraryMiningStatus, [{
    partnerId,
    integrationId: integrationApp,
    skipLoader: true,
  }]);
  if (response && response.jobId && !TERMINAL_STATUSES.has((response.lastStatus || '').toLowerCase())) {
    yield put({
      type: 'genAi_itineraryMining_startPolling',
      payload: { jobId: response.jobId },
    });
  }
}

function* pollJobStatus({ payload }) {
  const { jobId } = payload || {};
  if (!jobId) return;
  yield put({ type: 'genAi_itineraryMining_pollingStarted' });
  let lastStatus;
  let lastSignature;
  let stableCount = 0;
  const MAX_STABLE_POLLS = 12; // ~60s at 5s intervals
  const MAX_POLLS = 60; // ~5 minutes safeguard
  let polls = 0;
  try {
    while (true) {
      const currentJob = yield select(state => state?.generativeAi?.partnerSettings?.itineraryMining?.job?.jobId);
      const jobMismatch = currentJob !== undefined && currentJob !== jobId;
      if (jobMismatch) {
        break;
      }
      const { response, error } = yield call(matcherApis.checkStatus, {
        jobs: [{ jobId, jobType: 'pyFileMatch' }],
        skipLoader: true,
      });
      if (error) {
        yield put({
          type: 'genAi_itineraryMining_jobProgress',
          payload: { jobId, status: 'error', log: [`${error}`] },
        });
        break;
      }
      const jobResponse = Array.isArray(response) ? response[0] : null;
      if (jobResponse) {
        yield put({
          type: 'genAi_itineraryMining_jobProgress',
          payload: jobResponse,
        });
        const status = (jobResponse.status || '').toLowerCase();
        const logSignature = Array.isArray(jobResponse.log) ? jobResponse.log.join('|') : '';
        const processSignature = typeof jobResponse.process === 'string' ? jobResponse.process : '';
        const infoSignature = typeof jobResponse.info === 'string' ? jobResponse.info : '';
        const signature = `${status}::${logSignature}::${processSignature}::${infoSignature}`;
        if (status === lastStatus && signature === lastSignature) {
          stableCount += 1;
        } else {
          stableCount = 0;
          lastStatus = status;
          lastSignature = signature;
        }
        if (TERMINAL_STATUSES.has(status)) {
          // The persisted enrichment_query.json refresh is handled by
          // watchJobProgressForLastQueryRefresh, which observes the jobProgress
          // action we just dispatched above and fires once per terminal jobId.
          break;
        }
        if (stableCount >= MAX_STABLE_POLLS) {
          yield put({
            type: 'genAi_itineraryMining_jobProgress',
            payload: { jobId, status: 'error', log: ['Itinerary matching job is not progressing. Please retry.'] },
          });
          break;
        }
      } else {
        break;
      }
      polls += 1;
      if (polls >= MAX_POLLS) {
        yield put({
          type: 'genAi_itineraryMining_jobProgress',
          payload: { jobId, status: 'error', log: ['Timed out while waiting for itinerary matching job.'] },
        });
        break;
      }
      yield delay(5000);
    }
  } finally {
    yield put({ type: 'genAi_itineraryMining_pollingStopped' });
  }
}

function* cancelItineraryJob() {
  const context = yield call(getPartnerContext);
  if (!context) return;
  const { partnerId, integrationApp } = context;
  const job = yield select(state => state.generativeAi.partnerSettings.itineraryMining.job);
  if (!job || !job.jobId) return;
  const response = yield apiSaga('genAi_itineraryMining_cancelJob', generativeAi.itineraryMiningCancel, [{
    partnerId,
    integrationId: integrationApp,
    jobId: job.jobId,
    skipLoader: true,
  }]);
  if (response) {
    yield put({
      type: 'genAi_itineraryMining_jobProgress',
      payload: {
        jobId: job.jobId,
        status: response.status || 'cancelling',
        cooldownUntil: response.cooldownUntil,
      },
    });
  }
}

function* clearItineraryDatabase() {
  const context = yield call(getPartnerContext);
  if (!context) return;
  const { partnerId, integrationApp } = context;
  yield apiSaga('genAi_itineraryMining_clearDatabase', generativeAi.itineraryMiningClear, [{
    partnerId,
    integrationId: integrationApp,
    skipLoader: true,
  }]);
}

const itineraryMiningSagas = [
  takeLatest('genAi_itineraryMining_init', initItineraryMining),
  takeLatest('genAi_itineraryMining_loadAffiliateAgents', loadAffiliateAgents),
  takeLatest('genAi_itineraryMining_loadConfig', loadConfig),
  takeLatest('genAi_itineraryMining_saveConfig', saveConfig),
  takeLatest('genAi_itineraryMining_runJob', runItineraryMiningJob),
  takeLatest('genAi_itineraryMining_loadJobStatus', loadPersistedJobStatus),
  takeLatest('genAi_itineraryMining_startPolling', pollJobStatus),
  takeLatest('genAi_itineraryMining_toggleEnabled', toggleItineraryEnabled),
  takeLatest('genAi_itineraryMining_toggleUseSelectedAgentId', toggleUseSelectedAgentId),
  takeLatest('genAi_itineraryMining_cancelJob', cancelItineraryJob),
  takeLatest('genAi_itineraryMining_clearDatabase', clearItineraryDatabase),
  takeLatest('genAi_itineraryMining_loadLastQuery', loadLastEnrichmentQuery),
  watchJobProgressForLastQueryRefresh(),
];

export default itineraryMiningSagas;

export {
  saveConfig,
  loadConfig,
  runItineraryMiningJob,
  loadPersistedJobStatus,
  pollJobStatus,
  initItineraryMining,
  getPartnerContext,
  toggleUseSelectedAgentId,
  cancelItineraryJob,
  clearItineraryDatabase,
  loadAffiliateAgents,
  loadLastEnrichmentQuery,
  watchJobProgressForLastQueryRefresh,
};
